import { useEffect, useState } from "react";

import useLocalStorage, { readStored, writeStored } from "./useLocalStorage";

// Light / dark / auto for a lab, the same behaviour as the noun-gender labs' setting:
// stored per lab, applied as body.dark-mode (the class the whole app styles against), and
// written through to the account's game settings so it carries across Clozemaster.

export const THEMES = ["auto", "light", "dark"];
export const THEME_LABELS = { auto: "Auto", dark: "Dark", light: "Light" };

// "auto" follows the OS. Where the OS expresses no preference - or the browser can't be
// asked - fall back to dark rather than assuming light, as the other labs do.
function systemPrefersDark() {
  try {
    if (!window.matchMedia) return true;
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) return true;
    if (window.matchMedia("(prefers-color-scheme: light)").matches) return false;

    return true;
  } catch (e) {
    return true;
  }
}

function bodyIsDark() {
  try {
    return document.body.classList.contains("dark-mode");
  } catch (e) {
    return false;
  }
}

// A signed in user who has never touched this control should match the rest of
// Clozemaster, so adopt whatever the account rendered as an explicit choice.
function seedFromAccount(prefix, isSignedIn) {
  if (isSignedIn && readStored(prefix, "theme", null) === null) {
    writeStored(prefix, "theme", bodyIsDark() ? "dark" : "light");
  }

  return true;
}

export default function useTheme({ gameSettingsUrl, isSignedIn, prefix }) {
  // Before useLocalStorage below reads it, so the first render has the seeded value.
  useState(() => seedFromAccount(prefix, isSignedIn));

  const [theme, setTheme] = useLocalStorage(prefix, "theme", "auto");
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const dark = theme === "dark" || (theme === "auto" && systemDark);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", dark);
  }, [dark]);

  // On "auto", follow the OS while the page is open rather than only at load.
  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(systemPrefersDark());

    if (query.addEventListener) query.addEventListener("change", onChange);
    else if (query.addListener) query.addListener(onChange);

    return () => {
      if (query.removeEventListener) query.removeEventListener("change", onChange);
      else if (query.removeListener) query.removeListener(onChange);
    };
  }, []);

  const chooseTheme = (choice) => {
    setTheme(choice);
    if (!gameSettingsUrl) return;

    const choiceIsDark = choice === "dark" || (choice === "auto" && systemDark);
    const token = document.querySelector("meta[name=\"csrf-token\"]");

    fetch(gameSettingsUrl, {
      body: JSON.stringify({ settings: { theme: choiceIsDark ? "dark" : "light" } }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: "POST",
    }).catch(() => {
      // The page is already in the right theme; a failed write just means it won't
      // survive to the next page load.
    });
  };

  return { chooseTheme, systemDark, theme };
}
